/**
 * Amortization Schedule Table Component
 * Displays the amortization schedule for a contract
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon, Info as InfoIcon, PictureAsPdf as PdfIcon, Edit as EditIcon, Functions as FunctionsIcon, KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon, UnfoldMore as ExpandAllIcon, UnfoldLess as CollapseAllIcon, Flag as FlagIcon, AccountBalanceWallet as WalletIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { exportAmortizationSchedule } from '../../utils/excelExport';
import { formatNumber } from '../../utils/formatters';
import CalculationDetailPopover from './CalculationDetailPopover';
import { useTenant } from '../../contexts/TenantContext';

function AmortizationScheduleTable({ schedule: scheduleProp, variances, contractId, onRegenerate, canAdmin, contract = {}, currencyCode, onDownloadPDF, isGeneratingPDF, onOverrideRequest, onShowOverrides, readOnly = false, hideRegenerateButton = false }) {
  const { tenant } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [auditAnchorEl, setAuditAnchorEl] = useState(null);
  const [auditEntryId, setAuditEntryId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [paramsExpanded, setParamsExpanded] = useState(false);
  const [cashFlowExpanded, setCashFlowExpanded] = useState(false);

  const handleAuditClick = (event, entry) => {
    if (entry.id) {
      setAuditAnchorEl(event.currentTarget);
      setAuditEntryId(entry.id);
    }
  };

  // Render the per-row variance chip from the contract_payment_variance map.
  // Returns null when there's no variance row or the variance is < 0.01.
  // Sits beside the Status chip; does NOT replace it (regression-safe).
  const renderVarianceChip = (scheduleId) => {
    if (!variances || !scheduleId) return null;
    const v = variances.get(scheduleId);
    if (!v) return null;
    const amount = parseFloat(v.variance_amount);
    if (!Number.isFinite(amount) || Math.abs(amount) < 0.01) return null;
    const ccy = currencyCode || contract?.currency_code || '';
    const isShort = amount < 0;
    const tooltip = (
      <Box sx={{ p: 0.5 }}>
        {v.variance_reason && (
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
            {v.variance_reason}
          </Typography>
        )}
        <Typography variant="caption" sx={{ display: 'block' }}>
          Allocated interest: {ccy} {formatNumber(v.allocated_interest)}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          Allocated capital: {ccy} {formatNumber(v.allocated_capital)}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          Cash received: {ccy} {formatNumber(v.cash_received)}
        </Typography>
      </Box>
    );
    return (
      <Tooltip title={tooltip} arrow>
        <Chip
          label={`${isShort ? 'Short' : 'Over'} ${ccy} ${formatNumber(Math.abs(amount))}`}
          size="small"
          color={isShort ? 'error' : 'success'}
          variant="outlined"
          sx={{ ml: 0.5 }}
        />
      </Tooltip>
    );
  };

  const handleAuditClose = () => {
    setAuditAnchorEl(null);
    setAuditEntryId(null);
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // Ensure schedule is always a referentially stable array
  const schedule = useMemo(() => Array.isArray(scheduleProp) ? scheduleProp : [], [scheduleProp]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = () => {
    exportAmortizationSchedule(schedule, contract);
  };

  const formatCurrency = (value, showSign = false) => {
    if (!value && value !== 0) return '-';
    const num = parseFloat(value);
    const formatted = formatNumber(Math.abs(num), dp);
    if (showSign && num < 0) {
      return `(${formatted})`;
    }
    return formatted;
  };

  // Format amount with flow direction (negative for outflows)
  const formatFlowAmount = (value, flowType) => {
    if (!value && value !== 0) return '-';
    const num = parseFloat(value);
    if (num === 0) return '-';
    const formatted = formatNumber(num, 2);
    if (flowType === 'outflow') {
      return <span style={{ color: '#d32f2f' }}>({formatted})</span>;
    }
    if (flowType === 'inflow') {
      return <span style={{ color: '#1976d2' }}>{formatted}</span>;
    }
    return formatted;
  };

  // Render WHT amount for a row. Accrual rows render WHT as a plain positive
  // number. Payment rows render WHT in purple parens to indicate cash outflow
  // to the tax authority. The gross-up indicator (GU/NG/MX) is rendered in a
  // separate WHT Mode column, not here.
  const formatWhtForRow = (whtAmount, narration) => {
    const wht = parseFloat(whtAmount) || 0;
    if (wht <= 0) return '-';
    const amount = formatNumber(wht, 2);
    // T064: Interest Float rows are accrual-only sub-rows emitted between
    // rate changes inside a combined-payment period; treat like Interest Accrual.
    if (narration === 'Interest Accrual' || narration === 'Interest Float') {
      return amount;
    }
    return <span style={{ color: '#9c27b0' }}>({amount})</span>;
  };

  // Render the WHT Mode cell content for a row. Returns a GU / NG / MX label
  // with tooltip for any row that has WHT > 0 (accrual or payment); '-' otherwise.
  // narration is kept in the signature for call-site symmetry but is not used.
  // eslint-disable-next-line no-unused-vars
  const renderWhtMode = (grossUp, narration, whtAmount) => {
    if (!(parseFloat(whtAmount) > 0)) return '-';
    const label = grossUp === 'mixed' ? 'MX' : grossUp === true ? 'GU' : grossUp === false ? 'NG' : '-';
    if (label === '-') return '-';
    const tooltip = grossUp === 'mixed'
      ? 'Mixed gross-up treatment across investors'
      : grossUp === true
        ? 'Grossed up — WHT paid by company on top of interest'
        : "Not grossed up — WHT deducted from investor's interest";
    return (
      <Tooltip title={tooltip} arrow>
        <Box component="span" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>{label}</Box>
      </Tooltip>
    );
  };

  // Determine if entry is an inflow or outflow based on narration
  const getFlowType = (narration) => {
    if (!narration) return 'neutral';
    // Inflows - money coming IN from investor
    if (narration.includes('Drawdown') || narration.includes('Opening Balance') || narration.includes('Injection')) return 'inflow';
    // Outflows - money going OUT to investor
    if (narration.includes('Payment') || narration.includes('Fee') || narration.includes('Duty') || narration.includes('Withdrawal')) return 'outflow';
    // Accruals - not actual cash flow.
    // T064: Interest Float is an accrual-only sub-row inside combined-payment periods.
    if (narration.includes('Accrual') || narration === 'Interest Float') return 'accrual';
    return 'neutral';
  };

  const getNarrationColor = (narration) => {
    if (!narration) return 'default';
    // Carried Interest Accrual handled by getNarrationChipProps (filled purple).
    // Special handling for adhoc capital events
    if (narration.includes('Injection')) return 'success';
    if (narration.includes('Withdrawal')) return 'warning';
    // Standard flow type coloring
    const flowType = getFlowType(narration);
    if (flowType === 'inflow') return 'info';
    if (flowType === 'outflow') return 'error';
    if (flowType === 'accrual') return 'warning';
    if (narration.includes('Closing')) return 'default';
    return 'default';
  };

  // Returns the full prop set for the narration Chip so call sites stay one-liners.
  // Carried Interest Accrual is informational (carried from a prior contract);
  // render it as an outlined deep-purple chip so it stays consistent with the
  // outlined style of every other narration chip but reads as a distinct
  // category against the orange (Interest Accrual) and red (Interest Payment)
  // outlined chips around it.
  const getNarrationChipProps = (narration) => {
    if (narration === 'Carried Interest Accrual') {
      return {
        variant: 'outlined',
        sx: {
          color: '#7b1fa2',
          borderColor: '#7b1fa2',
          fontWeight: 600,
        },
      };
    }
    return {
      variant: 'outlined',
      color: getNarrationColor(narration),
    };
  };

  const getFlowLabel = (narration) => {
    const flowType = getFlowType(narration);
    if (flowType === 'inflow') return { label: 'IN', color: 'info' };
    if (flowType === 'outflow') return { label: 'OUT', color: 'error' };
    if (flowType === 'accrual') return { label: 'ACC', color: 'warning' };
    return { label: '-', color: 'default' };
  };

  // Check if schedule has investor data (multi-investor contract)
  const isMultiInvestor = schedule.some(entry => entry.investor_id != null);

  // Per-investor WHT breakdown for multi-investor schedules
  const investorWhtSummary = useMemo(() => {
    if (!isMultiInvestor) return [];
    const map = {};
    schedule.forEach(entry => {
      const id = entry.investor_id;
      if (id == null) return;
      if (!map[id]) {
        map[id] = {
          investor_id: id,
          investor_name: entry.investor_name || String(id),
          capital_amount: 0,
          gross_interest: 0,
          wht_total: 0,
          net_interest: 0,
          wht_rate: parseFloat(entry.wht_rate_applied) || 0,
        };
      }
      const flowType = getFlowType(entry.narration);
      if (flowType === 'inflow') {
        map[id].capital_amount += parseFloat(entry.capital_amount) || 0;
      }
      if (flowType === 'outflow') {
        const gross = parseFloat(entry.gross_interest_amount) || parseFloat(entry.interest_amount) || 0;
        const wht = parseFloat(entry.wht_amount) || 0;
        const net = parseFloat(entry.net_interest_amount) || parseFloat(entry.interest_amount) || 0;
        map[id].gross_interest += gross;
        map[id].wht_total += wht;
        map[id].net_interest += net;
      }
    });
    return Object.values(map);
  }, [isMultiInvestor, schedule]);

  // Show the WHT Mode column only when at least one row has WHT > 0.
  const showWhtMode = useMemo(
    () => (schedule || []).some(e => parseFloat(e.wht_amount) > 0),
    [schedule]
  );

  // Precompute running balance: previous balance + current row's total
  // Inflows (capital drawdown, injection) add; outflows (payments, withdrawals) subtract; accruals add
  // For outflows, use gross_interest_amount to symmetrically match what accruals add.
  // This correctly handles both gross-up (where net=gross, so net+wht > gross) and
  // non-gross-up (where net+wht = gross) WHT scenarios.
  const runningBalances = useMemo(() => {
    if (!schedule || schedule.length === 0) return [];
    const balances = [];
    let balance = 0;
    schedule.forEach((entry) => {
      const capital = parseFloat(entry.capital_amount) || 0;
      const interest = parseFloat(entry.interest_amount) || 0;
      const wht = parseFloat(entry.wht_amount) || 0;
      const total = capital + interest + wht;
      const flowType = getFlowType(entry.narration);

      if (flowType === 'inflow') {
        balance += total;
      } else if (flowType === 'outflow') {
        // Use gross_interest_amount for symmetric treatment with accruals.
        // Accruals add gross interest; payments must subtract gross interest.
        // For non-gross-up: gross = net + wht (same result as before).
        // For gross-up: net = gross and wht > 0, so (net + wht) would over-subtract.
        const grossInterest = parseFloat(entry.gross_interest_amount) || (interest + wht);
        balance -= (capital + grossInterest);
      } else if (flowType === 'accrual') {
        // Accrual rows carry interest_amount = gross_interest_amount and a separate
        // wht_amount for display. Adding `total` (= interest + wht) here would
        // double-count WHT, leaving it stranded after the matching payment subtracts
        // only gross. Mirror the outflow branch: add gross interest, not interest+wht.
        const grossInterest = parseFloat(entry.gross_interest_amount) || interest;
        balance += (capital + grossInterest);
      }

      balances.push(Math.round(balance * 100) / 100);
    });
    return balances;
  }, [schedule]);

  // Group multi-investor entries by payment_date + narration for readable display
  const groupedSchedule = useMemo(() => {
    if (!isMultiInvestor || !schedule || schedule.length === 0) return null;

    const groups = [];
    let currentGroup = null;

    schedule.forEach((entry, index) => {
      const taggedEntry = { ...entry, _originalIndex: index };
      const groupKey = `${entry.payment_date}|${entry.narration}`;

      if (currentGroup && currentGroup.groupKey === groupKey) {
        currentGroup.entries.push(taggedEntry);
        currentGroup.capital_amount += parseFloat(entry.capital_amount) || 0;
        currentGroup.interest_amount += parseFloat(entry.interest_amount) || 0;
        const entryNet = parseFloat(entry.net_interest_amount);
        currentGroup.net_interest_amount += Number.isFinite(entryNet) ? entryNet : (parseFloat(entry.interest_amount) || 0);
        currentGroup.wht_amount += parseFloat(entry.wht_amount) || 0;
        currentGroup.payment_amount += parseFloat(entry.payment_amount) || 0;
        currentGroup.lastEntryIndex = index;
        currentGroup.investorCount = currentGroup.entries.length;
        if (entry.has_override) currentGroup.hasAnyOverride = true;
        // Track if rates differ across investors
        const rate = parseFloat(entry.interest_rate);
        if (rate && rate !== currentGroup._firstRate) currentGroup._ratesDiffer = true;
        // Track if gross-up treatment differs across investors
        if (!!entry.wht_gross_up !== !!currentGroup.wht_gross_up) currentGroup._grossUpVaries = true;
        // Aggregate status: if any Pending, show Pending
        if (entry.status === 'Pending') currentGroup.status = 'Pending';
      } else {
        currentGroup = {
          groupKey,
          payment_date: entry.payment_date,
          period_start: entry.period_start,
          period_end: entry.period_end,
          narration: entry.narration,
          version_number: entry.version_number,
          interest_rate: entry.interest_rate,
          _firstRate: parseFloat(entry.interest_rate) || 0,
          _ratesDiffer: false,
          days_in_period: entry.days_in_period,
          status: entry.status,
          capital_amount: parseFloat(entry.capital_amount) || 0,
          interest_amount: parseFloat(entry.interest_amount) || 0,
          net_interest_amount: (() => {
            const n = parseFloat(entry.net_interest_amount);
            return Number.isFinite(n) ? n : (parseFloat(entry.interest_amount) || 0);
          })(),
          wht_amount: parseFloat(entry.wht_amount) || 0,
          wht_gross_up: !!entry.wht_gross_up,
          _grossUpVaries: false,
          payment_amount: parseFloat(entry.payment_amount) || 0,
          entries: [taggedEntry],
          lastEntryIndex: index,
          firstEntryIndex: index,
          investorCount: 1,
          hasAnyOverride: !!entry.has_override,
        };
        groups.push(currentGroup);
      }
    });

    return groups;
  }, [isMultiInvestor, schedule]);

  const handleExpandAllGroups = () => {
    if (!groupedSchedule) return;
    const allKeys = {};
    groupedSchedule.forEach(g => { allKeys[g.groupKey] = true; });
    setExpandedGroups(allKeys);
  };

  const handleCollapseAllGroups = () => {
    setExpandedGroups({});
  };

  if (!schedule || schedule.length === 0) {
    const getEmptyMessage = () => {
      switch (contract?.status) {
        case 'Draft':
          return 'Generate a preview of the amortization schedule for review.';
        case 'Pending Approval':
          return 'Generate or regenerate the schedule for approval review.';
        case 'Approved':
          return 'Activate the contract to generate the schedule, or generate it manually below.';
        case 'Active':
          return 'Click below to generate the amortization schedule.';
        default:
          return 'Schedule will be generated when contract is activated.';
      }
    };

    // Allow generation for Draft, Pending Approval, Approved, and Active statuses
    const canGenerate = ['Draft', 'Pending Approval', 'Approved', 'Active'].includes(contract?.status);

    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography color="textSecondary" gutterBottom>
            No amortization schedule available.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {getEmptyMessage()}
          </Typography>
          {canGenerate && onRegenerate && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={onRegenerate}
            >
              Generate Schedule
            </Button>
          )}
        </Box>
      </Paper>
    );
  }

  // Resolve currency label. Prop wins because some callers (wizard preview) build
  // the contract object from form state, which has investment_currency_id but not
  // the join-derived currency_code. Fallback to contract.currency_code for loaded
  // contracts (ContractDetail page, summary tab, etc.).
  const ccy = currencyCode || contract.currency_code || '';
  // Prefix that renders "SZL " before an amount, or nothing when no currency is known.
  // Used in JSX (e.g. <>{ccyPrefix}{formatNumber(...)}</>).
  const ccyPrefix = ccy ? `${ccy} ` : '';

  // Check if schedule has WHT data. Gate on a non-zero WHT amount on at least
  // one row — wht_rate_applied gets stored even when the rate is 0, so we
  // can't use it as a presence signal.
  const hasWhtData = schedule.some(entry => parseFloat(entry.wht_amount) > 0);

  // Check if any entries use gross-up (WHT is company expense, not deducted from investor)
  const hasGrossUp = schedule.some(entry => entry.wht_gross_up);

  // Extract WHT rate from schedule entries (first entry with a rate)
  const whtRateEntry = schedule.find(entry => entry.wht_rate_applied && parseFloat(entry.wht_rate_applied) > 0);
  const whtRate = whtRateEntry ? parseFloat(whtRateEntry.wht_rate_applied) : 0;
  const whtRateSource = whtRateEntry ? whtRateEntry.wht_rate_source : null;

  // Per-period overrides on Draft and Approved. Active contracts are altered
  // via Capital Amendment / Interest Payment / Finalisation flows. Pending
  // Approval is locked (approver's hands).
  const canOverride = canAdmin && onOverrideRequest && !readOnly
    && ['Draft', 'Approved'].includes(contract?.status);

  // Per-row overrides apply to Interest Payment and Combined Payment rows.
  // Combined Payment (T064) plays the same role as Interest Payment on
  // combine_payment_rows contracts — the backend amendment SPs treat them
  // equivalently. Accrual is calculated from contract terms; capital and
  // rate go via adhoc events.
  const isOverridable = (narration) => narration === 'Interest Payment' || narration === 'Combined Payment';

  // Calculate schedule totals with inflow/outflow separation
  const totals = schedule.reduce((acc, entry) => {
    const flowType = getFlowType(entry.narration);
    const capitalAmt = parseFloat(entry.capital_amount) || 0;
    const interestAmt = parseFloat(entry.interest_amount) || 0;
    const grossInterestAmt = parseFloat(entry.gross_interest_amount) || interestAmt;
    const whtAmt = parseFloat(entry.wht_amount) || 0;
    const netInterestAmt = parseFloat(entry.net_interest_amount) || interestAmt;
    const paymentAmt = parseFloat(entry.payment_amount) || 0;

    if (flowType === 'inflow') {
      acc.capitalInflow += capitalAmt;
    } else if (flowType === 'outflow') {
      acc.capitalOutflow += capitalAmt;
      acc.interestOutflow += interestAmt;
      acc.grossInterestOutflow += grossInterestAmt;
      acc.whtTotal += whtAmt;
      acc.netInterestOutflow += netInterestAmt;
    }
    acc.totalPayments += paymentAmt;
    return acc;
  }, { capitalInflow: 0, capitalOutflow: 0, interestOutflow: 0, grossInterestOutflow: 0, whtTotal: 0, netInterestOutflow: 0, totalPayments: 0 });

  return (
    <Box>
      {/* Contract Inputs Summary */}
      <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: paramsExpanded ? 2 : 0,
              cursor: 'pointer',
              px: 2,
              py: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.paper',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => setParamsExpanded(prev => !prev)}
          >
            <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" color="primary" sx={{ flexGrow: 1 }}>
              Schedule Parameters
            </Typography>
            <IconButton size="small" aria-label={paramsExpanded ? 'Collapse parameters' : 'Expand parameters'}>
              {paramsExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          </Box>
          <Collapse in={paramsExpanded} timeout="auto" unmountOnExit>
          <Grid container spacing={2}>
            {/* Investment Details */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Investment Details
              </Typography>
              <Grid container spacing={1}>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Capital Amount</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {ccyPrefix}{formatNumber(contract.capital_amount, 2)}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Initial Interest Rate</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {contract.initial_interest_rate ?? contract.interest_rate}% {contract.rate_type}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Current Interest Rate</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {contract.interest_rate}% {contract.rate_type}
                  </Typography>
                </Grid>
                {contract.rate_type === 'Variable' && contract.benchmark_rate_type && (
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Spread</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {contract.benchmark_rate_type}: {contract.benchmark_rate_value}%
                      {parseFloat(contract.rate_spread || 0) !== 0 && (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}({parseFloat(contract.rate_spread) >= 0 ? '+' : ''}{parseFloat(contract.rate_spread).toFixed(2)}% spread)
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                )}
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Term</Typography>
                  <Typography variant="body2">
                    {contract.investment_term_value} {contract.investment_term}
                    {contract.deposit_date && contract.maturity_date && (() => {
                      const start = new Date(contract.deposit_date);
                      const end = new Date(contract.maturity_date);
                      const totalDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
                      const basis = parseInt(contract.day_count_basis) || 365;
                      const years = (totalDays / basis).toFixed(2);
                      return <Typography variant="caption" color="textSecondary" component="span"> ({years} years / {totalDays} days)</Typography>;
                    })()}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Calculation Type</Typography>
                  <Typography variant="body2">
                    {contract.calculation_profile_interest_method
                      ? `${contract.calculation_profile_interest_method} Interest`
                      : contract.calculation_profile_name || 'Simple Interest'}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Day Count Basis</Typography>
                  <Typography variant="body2">
                    {contract.day_count_basis === '360' || contract.day_count_basis === 360
                      ? '360 days (Actual/360)'
                      : '365 days (Actual/365)'}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Interest Formula</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', backgroundColor: '#f5f5f5', p: 0.5, borderRadius: 0.5 }}>
                    {contract.calculation_profile_formula
                      || ((contract.calculation_profile_interest_method || 'Simple') === 'Compound'
                        ? `Interest = Principal x (1 + Rate/${contract.calculation_profile_compounding_frequency || 12})^(${contract.calculation_profile_compounding_frequency || 12} x Actual Days / ${contract.day_count_basis || 365}) - Principal`
                        : (contract.calculation_profile_interest_method === 'Compound Linear'
                          ? `Daily Amount = (Principal x (1 + Rate)^(Term Days / ${contract.day_count_basis || 365}) - Principal) / Term Days; Period Interest = Daily Amount x Days in Period`
                          : (contract.calculation_profile_interest_method === 'Simple Periodic Capitalization'
                            ? `Within period: Interest = Principal x Rate x (Actual Days / ${contract.day_count_basis || 365}). Principal capitalizes (steps up by accrued interest) at each period boundary.`
                            : `Interest = Principal x Rate x (Actual Days / ${contract.day_count_basis || 365})`)))}
                  </Typography>
                </Grid>
                {hasWhtData && (
                  <>
                    <Grid size={6}>
                      <Typography variant="caption" color="textSecondary">Withholding Tax Rate</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {whtRate > 0 ? `${whtRate.toFixed(2)}%` : 'None'}
                        </Typography>
                        {whtRateSource === 'override' && (
                          <Chip label="Override" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                        {(whtRateSource === 'rule_country' || whtRateSource === 'rule_default') && (
                          <Chip
                            label={whtRateSource === 'rule_country' ? 'Rule (Country)' : 'Rule (Default)'}
                            size="small"
                            color="info"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        {whtRateSource === 'investor_manual' && (
                          <Chip label="Manual" size="small" color="default" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                        {whtRateSource === 'fallback' && (
                          <Chip label="Fallback" size="small" color="default" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="textSecondary">Withholding Tax Gross-Up</Typography>
                      <Typography variant="body2">
                        <Chip
                          label={hasGrossUp ? 'Yes - Company Expense' : 'No - Deducted from Investor'}
                          size="small"
                          color={hasGrossUp ? 'info' : 'default'}
                          variant={hasGrossUp ? 'filled' : 'outlined'}
                          sx={{ height: 22 }}
                        />
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>

            {/* Dates */}
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Key Dates
              </Typography>
              <Grid container spacing={1}>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Start Date</Typography>
                  <Typography variant="body2">
                    {contract.deposit_date ? format(new Date(contract.deposit_date), 'dd MMM yyyy') : '-'}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Funds Received</Typography>
                  <Typography variant="body2">
                    {contract.funds_received_date
                      ? format(new Date(contract.funds_received_date), 'dd MMM yyyy')
                      : 'Same as deposit'}
                    {contract.funds_received_date && contract.funds_received_date !== contract.deposit_date && (
                      <Chip label="Interest start" size="small" color="info" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Maturity Date</Typography>
                  <Typography variant="body2">
                    {contract.maturity_date ? format(new Date(contract.maturity_date), 'dd MMM yyyy') : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Payment Frequencies */}
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Payment Frequencies
              </Typography>
              <Grid container spacing={1}>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Interest Paid</Typography>
                  <Typography variant="body2">
                    <Chip
                      label={contract.interest_paid_frequency || 'Monthly'}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ height: 22 }}
                    />
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Capital Repaid</Typography>
                  <Typography variant="body2">
                    <Chip
                      label={contract.capital_paid_frequency || 'At Maturity'}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ height: 22 }}
                    />
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          </Collapse>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mt: 1,
              mb: cashFlowExpanded ? 2 : 0,
              px: 2,
              py: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.paper',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => setCashFlowExpanded(prev => !prev)}
          >
            <WalletIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" color="primary" sx={{ flexGrow: 1 }}>
              Cash Flow Summary
            </Typography>
            <IconButton size="small" aria-label={cashFlowExpanded ? 'Collapse cash flow summary' : 'Expand cash flow summary'}>
              {cashFlowExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          </Box>
          <Collapse in={cashFlowExpanded} timeout="auto" unmountOnExit>
          <Divider sx={{ mb: 2 }} />

          {/* Schedule Totals */}
          {/* Cash flow cards — single flex row so any count (4 to 7 cards across
              WHT / non-WHT / with-variance / no-variance combinations) shares
              the row equally without wrapping. */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap' }}>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
              <Typography variant="caption" color="textSecondary">Capital Inflow</Typography>
              <Typography variant="body1" fontWeight="bold" color="info.main" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                + {ccyPrefix}{formatNumber(totals.capitalInflow, 2)}
              </Typography>
              <Typography variant="caption" color="textSecondary">From Investor</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #ef9a9a' }}>
              <Typography variant="caption" color="textSecondary">Capital Outflow</Typography>
              <Typography variant="body1" fontWeight="bold" color="error.main" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                - {ccyPrefix}{formatNumber(totals.capitalOutflow, 2)}
              </Typography>
              <Typography variant="caption" color="textSecondary">To Investor</Typography>
            </Box>
            {hasWhtData ? (
              <>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
                  <Typography variant="caption" color="textSecondary">Gross Interest</Typography>
                  <Typography variant="body1" fontWeight="bold" color="warning.dark" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    - {ccyPrefix}{formatNumber(totals.grossInterestOutflow, 2)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">Before Withholding Tax</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#f3e5f5', borderRadius: 1, border: '1px solid #ce93d8' }}>
                  <Typography variant="caption" color="textSecondary">{hasGrossUp ? 'WHT (Company Expense)' : 'Withholding Tax'}</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ color: '#9c27b0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {ccyPrefix}{formatNumber(totals.whtTotal, 2)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">{hasGrossUp ? 'Not Deducted' : 'Tax Withheld'}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#e8f5e9', borderRadius: 1, border: '1px solid #a5d6a7' }}>
                  <Typography variant="caption" color="textSecondary">Net Interest</Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    - {ccyPrefix}{formatNumber(totals.netInterestOutflow, 2)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">To Investor</Typography>
                </Box>
              </>
            ) : (
              <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
                <Typography variant="caption" color="textSecondary">Interest Paid</Typography>
                <Typography variant="body1" fontWeight="bold" color="warning.dark" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  - {ccyPrefix}{formatNumber(totals.interestOutflow, 2)}
                </Typography>
                <Typography variant="caption" color="textSecondary">Return to Investor</Typography>
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="textSecondary">Net Flow</Typography>
              <Typography variant="body1" fontWeight="bold" color={totals.capitalInflow - totals.capitalOutflow - totals.interestOutflow >= 0 ? 'success.main' : 'error.main'} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {ccyPrefix}{formatNumber(totals.capitalInflow - totals.capitalOutflow - totals.interestOutflow, 2)}
              </Typography>
              <Typography variant="caption" color="textSecondary">At End of Term</Typography>
            </Box>
            {(() => {
              // Cumulative variance card — render only when there's any
              // recorded variance against this contract.
              if (!variances || variances.size === 0) return null;
              let net = 0;
              let count = 0;
              variances.forEach((v) => {
                const amt = parseFloat(v?.variance_amount);
                if (Number.isFinite(amt) && Math.abs(amt) >= 0.01) {
                  net += amt;
                  count += 1;
                }
              });
              if (count === 0) return null;
              const isShort = net < 0;
              return (
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 1, backgroundColor: isShort ? '#ffebee' : '#e8f5e9', borderRadius: 1, border: `1px solid ${isShort ? '#ef9a9a' : '#a5d6a7'}` }}>
                  <Typography variant="caption" color="textSecondary">Cumulative Variance</Typography>
                  <Typography variant="body1" fontWeight="bold" color={isShort ? 'error.main' : 'success.main'} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {isShort ? '-' : '+'} {ccyPrefix}{formatNumber(Math.abs(net), 2)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {count} {count === 1 ? 'varied payment' : 'varied payments'}
                  </Typography>
                </Box>
              );
            })()}
          </Box>
          {isMultiInvestor && investorWhtSummary.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Per-Investor WHT Breakdown
                </Typography>
                <TableContainer component={Box}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.75, px: 1, fontSize: '0.78rem' } }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell><strong>Investor</strong></TableCell>
                        <TableCell align="right"><strong>Capital</strong></TableCell>
                        <TableCell align="right"><strong>WHT Rate</strong></TableCell>
                        <TableCell align="right"><strong>Gross Interest</strong></TableCell>
                        <TableCell align="right"><strong>WHT</strong></TableCell>
                        <TableCell align="right"><strong>Net Interest</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {investorWhtSummary.map((inv) => (
                        <TableRow key={inv.investor_id} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                          <TableCell>{inv.investor_name}</TableCell>
                          <TableCell align="right">{ccyPrefix}{formatNumber(inv.capital_amount, 2)}</TableCell>
                          <TableCell align="right">{inv.wht_rate > 0 ? `${inv.wht_rate.toFixed(2)}%` : '—'}</TableCell>
                          <TableCell align="right">{ccyPrefix}{formatNumber(inv.gross_interest, 2)}</TableCell>
                          <TableCell align="right" sx={{ color: '#9c27b0' }}>
                            {inv.wht_total > 0 ? <>({ccyPrefix}{formatNumber(inv.wht_total, 2)})</> : '—'}
                          </TableCell>
                          <TableCell align="right">{ccyPrefix}{formatNumber(inv.net_interest, 2)}</TableCell>
                        </TableRow>
                      ))}
                      {investorWhtSummary.length > 1 && (
                        <TableRow sx={{ backgroundColor: '#f0f4ff', fontWeight: 'bold' }}>
                          <TableCell><strong>Total</strong></TableCell>
                          <TableCell align="right">
                            <strong>{ccyPrefix}{formatNumber(investorWhtSummary.reduce((s, i) => s + i.capital_amount, 0), 2)}</strong>
                          </TableCell>
                          <TableCell align="right">—</TableCell>
                          <TableCell align="right">
                            <strong>{ccyPrefix}{formatNumber(investorWhtSummary.reduce((s, i) => s + i.gross_interest, 0), 2)}</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#9c27b0' }}>
                            <strong>({ccyPrefix}{formatNumber(investorWhtSummary.reduce((s, i) => s + i.wht_total, 0), 2)})</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>{ccyPrefix}{formatNumber(investorWhtSummary.reduce((s, i) => s + i.net_interest, 0), 2)}</strong>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
          </Collapse>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            Amortization Schedule ({groupedSchedule
              ? `${groupedSchedule.length} periods, ${schedule.length} entries`
              : `${schedule.length} entries`})
          </Typography>
          {groupedSchedule && (
            <>
              <Tooltip title="Expand all groups">
                <IconButton size="small" onClick={handleExpandAllGroups}>
                  <ExpandAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Collapse all groups">
                <IconButton size="small" onClick={handleCollapseAllGroups}>
                  <CollapseAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onDownloadPDF && (
            <Button
              variant="outlined"
              size="small"
              startIcon={isGeneratingPDF ? <CircularProgress size={16} /> : <PdfIcon />}
              onClick={onDownloadPDF}
              color="error"
              disabled={isGeneratingPDF}
            >
              {isGeneratingPDF ? 'Generating...' : 'Download Schedule PDF'}
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export to Excel
          </Button>
          {canAdmin && onRegenerate && !readOnly && !hideRegenerateButton && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onRegenerate}
              color="warning"
            >
              Regenerate
            </Button>
          )}
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {groupedSchedule && <TableCell sx={{ width: 40, p: 0.5 }}></TableCell>}
              <TableCell>Seq</TableCell>
              <TableCell align="center">Version</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Payment Date</TableCell>
              {isMultiInvestor && <TableCell>Investor</TableCell>}
              <TableCell align="center">Flow</TableCell>
              <TableCell>Narration</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Rate %</TableCell>
              <TableCell align="right">Days</TableCell>
              <TableCell align="right">Capital Amount</TableCell>
              <TableCell align="right">Interest Amount</TableCell>
              {hasWhtData && <TableCell align="right">Withholding Tax</TableCell>}
              {showWhtMode && <TableCell align="center">WHT Mode</TableCell>}
              <TableCell align="right">Payment Amount</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Outstanding Balance</TableCell>
              {canOverride && <TableCell align="center" sx={{ width: 40 }}></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedSchedule ? (
              /* Grouped multi-investor rendering */
              (groupedSchedule
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((group, groupIndex) => {
                  const flowType = getFlowType(group.narration);
                  const flowLabel = getFlowLabel(group.narration);
                  const isExpanded = !!expandedGroups[group.groupKey];
                  const isSingleEntry = group.investorCount === 1;
                  // Total = investor take-home: capital + net_interest_amount.
                  // For GU rows net == gross interest (no deduction). For NG rows
                  // net == gross − wht. For mixed groups this sums per-investor
                  // take-homes (net_interest_amount is summed in the reducer above).
                  const groupTotal = group.capital_amount + (group.net_interest_amount || 0);

                  return (
                    <React.Fragment key={group.groupKey}>
                      {/* Summary row */}
                      <TableRow
                        hover
                        sx={{
                          cursor: isSingleEntry ? 'default' : 'pointer',
                          backgroundColor: group.hasAnyOverride ? '#fff8e1' : '#f0f4ff',
                          '&:hover': { backgroundColor: group.hasAnyOverride ? '#fff3cd' : '#e3eafc' },
                        }}
                        onClick={isSingleEntry ? undefined : () => toggleGroup(group.groupKey)}
                      >
                        <TableCell sx={{ width: 40, p: 0.5 }}>
                          {!isSingleEntry && (
                            <IconButton size="small" sx={{ p: 0.25 }}>
                              {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell><strong>{page * rowsPerPage + groupIndex + 1}</strong></TableCell>
                        <TableCell align="center">
                          {group.version_number ? (
                            <Chip label={`V${group.version_number}`} size="small" color="info" variant="outlined" />
                          ) : '-'}
                        </TableCell>
                        <TableCell>{group.period_start ? new Date(group.period_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</TableCell>
                        <TableCell>{group.period_end ? new Date(group.period_end).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</TableCell>
                        <TableCell>
                          {group.payment_date ? format(new Date(group.payment_date), 'dd MMM yyyy') : '-'}
                        </TableCell>
                        <TableCell>
                          {isSingleEntry ? (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              {group.entries[0].investor_name || '-'}
                            </Typography>
                          ) : (
                            <Chip
                              label={`${group.investorCount} investors`}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={flowLabel.label} size="small" color={flowLabel.color} sx={{ minWidth: 40, fontWeight: 'bold' }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={group.narration || 'N/A'} size="small" {...getNarrationChipProps(group.narration)} />
                        </TableCell>
                        <TableCell align="center">
                          {(() => {
                            const varianceChip = group.entries?.length === 1 ? renderVarianceChip(group.entries[0]?.id) : null;
                            if (!group.status && !varianceChip) {
                              return <Typography variant="caption" color="textSecondary">-</Typography>;
                            }
                            return (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {group.status && (
                                  <Chip
                                    label={group.status}
                                    size="small"
                                    color={group.status === 'Paid' || group.status === 'Received' ? 'success' : group.status === 'Pending' ? 'warning' : 'default'}
                                    variant={group.status === 'Pending' ? 'outlined' : 'filled'}
                                    sx={{ minWidth: 70 }}
                                  />
                                )}
                                {varianceChip}
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          {group._ratesDiffer ? (
                            <Tooltip title="Rates differ across investors - expand to see details" arrow>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', cursor: 'help' }}>Mixed</Typography>
                            </Tooltip>
                          ) : (
                            group.interest_rate ? parseFloat(group.interest_rate).toFixed(2) : '-'
                          )}
                        </TableCell>
                        <TableCell align="right">{group.days_in_period || '-'}</TableCell>
                        <TableCell align="right">
                          <strong>{formatFlowAmount(group.capital_amount, flowType)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatFlowAmount(group.interest_amount, flowType)}</strong>
                          {group.hasAnyOverride && (
                            <Chip label="Override" size="small" color="warning" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                          )}
                        </TableCell>
                        {hasWhtData && (
                          <TableCell align="right">
                            {group.wht_amount && Math.abs(group.wht_amount) > 0
                              ? formatWhtForRow(Math.abs(group.wht_amount), group.narration)
                              : '-'}
                          </TableCell>
                        )}
                        {showWhtMode && (
                          <TableCell align="center">
                            {renderWhtMode(
                              group._grossUpVaries ? 'mixed' : group.wht_gross_up,
                              group.narration,
                              Math.abs(group.wht_amount)
                            )}
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <strong>{formatFlowAmount(group.payment_amount, flowType)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          {groupTotal === 0 ? '-' : <strong>{formatFlowAmount(groupTotal, flowType)}</strong>}
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(runningBalances[group.lastEntryIndex])}</strong>
                        </TableCell>
                        {canOverride && <TableCell />}
                      </TableRow>

                      {/* Expanded detail rows per investor */}
                      {isExpanded && !isSingleEntry && group.entries.map((entry, entryIdx) => {
                        const entryFlowType = getFlowType(entry.narration);
                        const hasOverride = entry.has_override;
                        const overrideTooltip = hasOverride
                          ? `Overridden: original interest ${entry.original_interest_amount != null ? 'R ' + formatNumber(parseFloat(entry.original_interest_amount), 2) : ''}`
                          : '';

                        return (
                          <TableRow
                            key={entry.id || `${group.groupKey}-${entryIdx}`}
                            sx={{
                              backgroundColor: hasOverride ? '#fff8e1' : (entryIdx % 2 === 0 ? '#fafcff' : '#ffffff'),
                              borderLeft: '3px solid #1976d2',
                              '& .MuiTableCell-root': { py: 0.5, fontSize: '0.8rem' },
                            }}
                          >
                            <TableCell sx={{ width: 40 }} />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, pl: 0.5 }}>
                                {entry.investor_name || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell align="center">
                              {(() => {
                                const varianceChip = renderVarianceChip(entry.id);
                                if (!entry.status && !varianceChip) {
                                  return <Typography variant="caption" color="textSecondary">-</Typography>;
                                }
                                return (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                    {entry.status && (
                                      <Chip
                                        label={entry.status}
                                        size="small"
                                        color={entry.status === 'Paid' || entry.status === 'Received' ? 'success' : entry.status === 'Pending' ? 'warning' : 'default'}
                                        variant={entry.status === 'Pending' ? 'outlined' : 'filled'}
                                        sx={{ minWidth: 60, height: 20, fontSize: '0.7rem' }}
                                      />
                                    )}
                                    {varianceChip}
                                  </Box>
                                );
                              })()}
                            </TableCell>
                            <TableCell align="right">
                              {entry.interest_rate ? parseFloat(entry.interest_rate).toFixed(2) : '-'}
                            </TableCell>
                            <TableCell />
                            <TableCell align="right">
                              {formatFlowAmount(entry.capital_amount, entryFlowType)}
                            </TableCell>
                            <TableCell align="right">
                              {hasOverride && entry.original_interest_amount != null ? (
                                <Tooltip title={overrideTooltip} arrow>
                                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    {formatFlowAmount(entry.interest_amount, entryFlowType)}
                                    <Chip label="Override" size="small" color="warning" sx={{ height: 14, fontSize: '0.55rem' }} />
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                                  {formatFlowAmount(entry.interest_amount, entryFlowType)}
                                  {entry.id && entry.narration === 'Interest Accrual' && (
                                    <Tooltip title="View calculation detail" arrow>
                                      <FunctionsIcon
                                        sx={{ fontSize: 12, color: 'action.disabled', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                        onClick={(e) => { e.stopPropagation(); handleAuditClick(e, entry); }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            {hasWhtData && (
                              <TableCell align="right">
                                {formatWhtForRow(entry.wht_amount, entry.narration)}
                              </TableCell>
                            )}
                            {showWhtMode && (
                              <TableCell align="center">
                                {renderWhtMode(entry.wht_gross_up, entry.narration, entry.wht_amount)}
                              </TableCell>
                            )}
                            <TableCell align="right">
                              {formatFlowAmount(entry.payment_amount, entryFlowType)}
                            </TableCell>
                            <TableCell align="right">
                              {(() => {
                                // Total = investor take-home: capital + net_interest_amount.
                                // GU: net = gross interest. NG: net = gross − wht.
                                const capital = parseFloat(entry.capital_amount) || 0;
                                const net = parseFloat(entry.net_interest_amount);
                                const netSafe = Number.isFinite(net) ? net : (parseFloat(entry.interest_amount) || 0);
                                const total = capital + netSafe;
                                if (total === 0) return '-';
                                return formatFlowAmount(total, entryFlowType);
                              })()}
                            </TableCell>
                            <TableCell />
                            {canOverride && (
                              <TableCell align="center" sx={{ p: 0 }}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                  {entry.has_override && onShowOverrides && (
                                    <Tooltip title="View / revert overrides on this row" arrow>
                                      <IconButton
                                        size="small"
                                        color="warning"
                                        onClick={(e) => { e.stopPropagation(); onShowOverrides(entry); }}
                                        sx={{ p: 0.25 }}
                                      >
                                        <FlagIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {isOverridable(entry.narration) && (
                                    <Tooltip title="Apply override" arrow>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => { e.stopPropagation(); onOverrideRequest(entry); }}
                                        sx={{ p: 0.25 }}
                                      >
                                        <EditIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                }))
            ) : (
              /* Flat single-investor rendering (unchanged) */
              (schedule
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((entry, index) => {
                  const flowType = getFlowType(entry.narration);
                  const flowLabel = getFlowLabel(entry.narration);

                  const hasOverride = entry.has_override;

                  return (
                    <TableRow
                      key={entry.id || index}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: hasOverride ? '#fff8e1' : '#fafafa' },
                        backgroundColor: hasOverride ? '#fff8e1' : undefined,
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell align="center">
                        {entry.version_number ? (
                          <Chip
                            label={`V${entry.version_number}`}
                            size="small"
                            color="info"
                            variant="outlined"
                            title={entry.version_reason || 'Rate Change'}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>{entry.period_start ? new Date(entry.period_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</TableCell>
                      <TableCell>{entry.period_end ? new Date(entry.period_end).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</TableCell>
                      <TableCell>
                        {entry.payment_date ? format(new Date(entry.payment_date), 'dd MMM yyyy') : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={flowLabel.label}
                          size="small"
                          color={flowLabel.color}
                          sx={{ minWidth: 40, fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.narration || 'N/A'}
                          size="small"
                          {...getNarrationChipProps(entry.narration)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const varianceChip = renderVarianceChip(entry.id);
                          if (!entry.status && !varianceChip) {
                            return <Typography variant="caption" color="textSecondary">-</Typography>;
                          }
                          return (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                              {entry.status && (
                                <Chip
                                  label={entry.status}
                                  size="small"
                                  color={
                                    entry.status === 'Paid' || entry.status === 'Received' ? 'success' :
                                    entry.status === 'Pending' ? 'warning' : 'default'
                                  }
                                  variant={entry.status === 'Pending' ? 'outlined' : 'filled'}
                                  sx={{ minWidth: 70 }}
                                />
                              )}
                              {varianceChip}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        {entry.interest_rate ? parseFloat(entry.interest_rate).toFixed(2) : '-'}
                      </TableCell>
                      <TableCell align="right">{entry.days_in_period || '-'}</TableCell>
                      <TableCell align="right">
                        {formatFlowAmount(entry.capital_amount, flowType)}
                      </TableCell>
                      <TableCell align="right">
                        {hasOverride && entry.original_interest_amount != null ? (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Typography component="span" variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '0.75rem' }}>
                              {formatNumber(parseFloat(entry.original_interest_amount), 2)}
                            </Typography>
                            <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                              {formatFlowAmount(entry.interest_amount, flowType)}
                            </Typography>
                          </Box>
                        ) : (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, justifyContent: 'flex-end' }}>
                            {parseFloat(entry.residual_carried_in || 0) !== 0 && (
                              <Tooltip title={`Includes R ${formatNumber(parseFloat(entry.residual_carried_in), 2)} rolled in from a prior override`} arrow>
                                <Chip
                                  label={`${parseFloat(entry.residual_carried_in) >= 0 ? '+' : ''}R ${formatNumber(parseFloat(entry.residual_carried_in), 2)}`}
                                  size="small"
                                  color="info"
                                  sx={{ height: 16, fontSize: '0.6rem' }}
                                />
                              </Tooltip>
                            )}
                            {formatFlowAmount(entry.interest_amount, flowType)}
                            {entry.id && entry.narration === 'Interest Accrual' && (
                              <Tooltip title="View calculation detail" arrow>
                                <FunctionsIcon
                                  sx={{ fontSize: 14, color: 'action.disabled', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                  onClick={(e) => handleAuditClick(e, entry)}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      {hasWhtData && (
                        <TableCell align="right">
                          {formatWhtForRow(entry.wht_amount, entry.narration)}
                        </TableCell>
                      )}
                      {showWhtMode && (
                        <TableCell align="center">
                          {renderWhtMode(entry.wht_gross_up, entry.narration, entry.wht_amount)}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <strong>{formatFlowAmount(entry.payment_amount, flowType)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          // Total = investor take-home: capital + net_interest_amount.
                          // For GU rows net = gross interest (no deduction).
                          // For NG rows net = gross − wht.
                          const capital = parseFloat(entry.capital_amount) || 0;
                          const net = parseFloat(entry.net_interest_amount);
                          const netSafe = Number.isFinite(net) ? net : (parseFloat(entry.interest_amount) || 0);
                          const total = capital + netSafe;
                          if (total === 0) return '-';
                          return formatFlowAmount(total, flowType);
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(runningBalances[page * rowsPerPage + index])}</strong>
                      </TableCell>
                      {canOverride && (
                        <TableCell align="center" sx={{ p: 0 }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            {hasOverride && onShowOverrides && (
                              <Tooltip title="View / revert overrides on this row" arrow>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => onShowOverrides(entry)}
                                  sx={{ p: 0.25 }}
                                >
                                  <FlagIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isOverridable(entry.narration) && (
                              <Tooltip title="Apply override" arrow>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => onOverrideRequest(entry)}
                                  sx={{ p: 0.25 }}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                }))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={groupedSchedule ? groupedSchedule.length : schedule.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="caption" display="block" gutterBottom>
          <strong>Legend:</strong>
        </Typography>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant="caption" display="block">
              <Chip label="IN" size="small" color="info" sx={{ mr: 1, height: 18 }} />
              Capital Inflow - Money received from investor
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              <Chip label="OUT" size="small" color="error" sx={{ mr: 1, height: 18 }} />
              Capital/Interest Outflow - Money paid to investor
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              <Chip label="ACC" size="small" color="warning" sx={{ mr: 1, height: 18 }} />
              Accrual - Interest accrued (non-cash entry)
            </Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant="caption" display="block">
              • <span style={{ color: '#1976d2' }}>Blue amounts</span> = Inflows
            </Typography>
            <Typography variant="caption" display="block">
              • <span style={{ color: '#d32f2f' }}>(Red amounts in parentheses)</span> = Outflows
            </Typography>
            {hasWhtData && (
              <Typography variant="caption" display="block">
                • <span style={{ color: '#9c27b0' }}>(Purple amounts)</span> = Withholding Tax deducted
              </Typography>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              • <Chip label="Adhoc Capital Injection" size="small" color="success" variant="outlined" sx={{ mr: 0.5, height: 18 }} /> Additional capital added
            </Typography>
            <Typography variant="caption" display="block">
              • <Chip label="Adhoc Capital Withdrawal" size="small" color="warning" variant="outlined" sx={{ mr: 0.5, height: 18 }} /> Capital returned early
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              • <Box component="span" sx={{ backgroundColor: '#fff8e1', px: 0.5, borderRadius: 0.5 }}>Yellow rows</Box> = Manual override applied
              {' '}<Chip label="Override" size="small" color="warning" sx={{ height: 16, fontSize: '0.6rem' }} />
            </Typography>
            <Typography variant="caption" display="block">
              • <FunctionsIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} /> Click formula icon on interest amounts to view calculation detail
            </Typography>
          </Grid>
        </Grid>
      </Box>
      {/* Calculation Detail Popover */}
      <CalculationDetailPopover
        anchorEl={auditAnchorEl}
        onClose={handleAuditClose}
        entryId={auditEntryId}
        contractId={contractId}
      />
    </Box>
  );
}

export default AmortizationScheduleTable;
